import React, {useEffect, useState} from 'react';
import {handleCheckboxChange, handleDateInputChange, handleInputChange} from "src/components/form/formHandler";
import userApi from "src/api/user/userApi";
import mailSubscriptionItemApi from "src/api/mail/mailSubscriptionItem";
import {handleApiError} from "src/api/errorHandler";
import {
  Alert, Button, Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel, Radio, RadioGroup,
  TextField
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {apiDateTimeStringToDate, dateToApiDateString} from "src/api/apiDateTimeHandler";
import Grid2 from "@mui/material/Unstable_Grid2";
import PasswordField from "src/components/form/PasswordField";
import useAuth from "src/hooks/use-auth";
import getIdFromIri from "src/functions/iri-converter";
import {useSearchParams} from "react-router-dom";

const UserForm = ({register}) => {
  const initialFormValues = {
    'email': '',
    'password': '',
    'repeatPassword': '',
    'firstName': '',
    'lastName': '',
    'lastNamePrefix': '',
    'phone': '',
    'sex': null,
    'dateOfBirth': null,
    'street': '',
    'houseNumber': '',
    'postalCode': '',
    'city': '',
    'iban': '',
    'ibanInTheNameOf': '',
    'locationMailSubscriptionItem': null,
    'onlineMailSubscriptionItem': null,
    'referralCode': '',
    'acknowledgeRules': false,
  };
  const [formValues, setFormValues] = useState(initialFormValues)
  const [user, setUser] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [mailSubscriptionItems, setMailSubscriptionItems] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showRegistrationSuccessMessage, setRegistrationSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const { myUser } = useAuth();
  let [searchParams] = useSearchParams();

  const fetchMailSubscriptionItems = async () => {
    const fetchedMailSubscriptionItems = await mailSubscriptionItemApi.retrieveCollection();
    setMailSubscriptionItems(fetchedMailSubscriptionItems['hydra:member']);
  }

  const setMyUser = () => {
    if (myUser) {
      setUser(myUser);
      setFormValues(prevState => ({
        ...prevState,
        'email': myUser.email,
        'firstName': myUser.firstName,
        'lastName': myUser.lastName,
        'lastNamePrefix': myUser.lastNamePrefix,
        'phone': myUser.phone,
        'sex': myUser.sex,
        'dateOfBirth': apiDateTimeStringToDate(myUser.dateOfBirth),
        'street': myUser.street,
        'houseNumber': myUser.houseNumber,
        'postalCode': myUser.postalCode,
        'city': myUser.city,
        'iban': myUser.iban,
        'ibanInTheNameOf': myUser.ibanInTheNameOf,
      }));
    }
  }

  useEffect(() => {
    fetchMailSubscriptionItems();
  }, [])

  useEffect(() => {
    const referralCode = searchParams.get('meeneem-actie-code');
    if (referralCode) {
      setFormValues(prevState => ({
        ...prevState,
        referralCode: referralCode
      }))
    }
  }, [])

  useEffect(() => {
    if (!register) {
      setMyUser();
    }
    if (myUser && myUser.mailSubscriptionItems && myUser.mailSubscriptionItems.length > 0) {
      const fetchUserMailSubscriptionItems = async () => {
        const fetchedMailSubscriptionItemsResponse = await mailSubscriptionItemApi.retrieveCollection({
          'id[]': myUser.mailSubscriptionItems.map(mailSubscriptionItem => Number(getIdFromIri(mailSubscriptionItem)))
        })
        const fetchedMailSubscriptionItems = fetchedMailSubscriptionItemsResponse['hydra:member'];
        setFormValues(prevState => ({
          ...prevState,
          locationMailSubscriptionItem: fetchedMailSubscriptionItems.find(mailSubscriptionItem => mailSubscriptionItem.type === 'physical_location')?.['@id'] ?? null,
          onlineMailSubscriptionItem: fetchedMailSubscriptionItems.find(mailSubscriptionItem => mailSubscriptionItem.type === 'online_area')?.['@id'] ?? null,
        }))
      }
      fetchUserMailSubscriptionItems();
    }
  }, [myUser])

  const handleMailSubscriptionItemChange = (e, formValueName) => {
    if (e.target.checked) {
      // Add to array
      setFormValues((prev) => ({
        ...prev,
        [formValueName]: [
          ...prev[formValueName],
          e.target.name,
        ]
      }));

      return
    }

    // Remove from array
    setFormValues((prev) => ({
      ...prev,
      [formValueName]: prev[formValueName]
        .filter(mailSubscriptionItem => mailSubscriptionItem !== e.target.name)
    }));
  }

  const handleRadioChange = (e) => {
    setFormValues(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value === '' ? null : e.target.value,
    }))
  }

  const getDataFromForm = (register) => {
    const data = {
      'email': formValues.email,
      'firstName': formValues.firstName,
      'lastName': formValues.lastName,
      'lastNamePrefix': formValues.lastNamePrefix,
      'phone': formValues.phone,
      'sex': formValues.sex,
      'dateOfBirth': null === formValues.dateOfBirth ? null : dateToApiDateString(formValues.dateOfBirth),
      'street': formValues.street,
      'houseNumber': formValues.houseNumber,
      'postalCode': formValues.postalCode,
      'city': formValues.city,
      'iban': formValues.iban,
      'ibanInTheNameOf': formValues.ibanInTheNameOf,
      'mailSubscriptionItems': [formValues.locationMailSubscriptionItem, formValues.onlineMailSubscriptionItem].filter(i => i !== null),
      registerReferralCode: formValues.referralCode ? formValues.referralCode : null,
      acknowledgeRules: Boolean(formValues.acknowledgeRules)
    }

    if (register) {
      data.plainPassword = formValues.password;
      data.repeatPassword = formValues.repeatPassword;
      data.iban = formValues.iban;
      data.ibanInTheNameOf = formValues.ibanInTheNameOf;
    }

    return data;
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegistrationSuccessMessage(false);
    setShowErrorMessage(false);
    setFormErrors({});

    const data = getDataFromForm(true);

    try {
      await userApi.register(data)
    } catch (e) {
      handleApiError(e, setFormErrors);
      setShowErrorMessage(true);
      return;
    }

    setRegistrationSuccessMessage(true);
    setFormValues(initialFormValues);
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setFormErrors({});

    const data = getDataFromForm(false);

    try {
      await userApi.update(user.id, data)
    } catch (e) {
      handleApiError(e, setFormErrors)
      setShowErrorMessage(true);
      return;
    }

    setShowSuccessMessage(true);
  }

  return (
    <form>

      <Grid2 container spacing={1}>
        <Grid2 xs={12}>
          <TextField
            value={formValues.email}
            name="email"
            label="E-mailadres"
            onChange={(e) => handleInputChange(e, formValues, setFormValues)}
            error={'email' in formErrors}
            helperText={formErrors.email}
            autoComplete='username'
            margin="dense"
            fullWidth
            required
          />
          <FormHelperText>Let op, dit e-mailadres wordt gebruikt om in te loggen.</FormHelperText>
        </Grid2>

        {register && (
          <>
            <Grid2 xs={12}>
              <PasswordField
                value={formValues.password}
                name="password"
                type="password"
                label="Wachtwoord"
                onChange={(e) => handleInputChange(e, formValues, setFormValues)}
                error={'plainPassword' in formErrors}
                helperText={formErrors.plainPassword ? formErrors.plainPassword : 'Wachtwoord moet ten minste één kleine, één hoofdletter en één cijfer bevatten en ten minste 12 tekens bevatten.'}
                autoComplete='new-password'
                margin="dense"
                fullWidth
                required
              />
            </Grid2>

            <Grid2 xs={12}>
              <PasswordField
                value={formValues.repeatPassword}
                name="repeatPassword"
                type="password"
                label="Wachtwoord bevestigen"
                onChange={(e) => handleInputChange(e, formValues, setFormValues)}
                error={'repeatPassword' in formErrors}
                helperText={formErrors.repeatPassword}
                autoComplete='new-password'
                margin="dense"
                fullWidth
                required
              />
            </Grid2>
          </>
        )}

        <Grid2 xs={12}>
        <TextField
          value={formValues.firstName}
          name="firstName"
          label="Voornaam"
          onChange={(e) => handleInputChange(e, formValues, setFormValues)}
          error={'firstName' in formErrors}
          helperText={formErrors.firstName}
          margin="dense"
          fullWidth
          required
        />
        </Grid2>

        <Grid2 xs={12} sm={4}>
          <TextField
            value={formValues.lastNamePrefix}
            name="lastNamePrefix"
            label="Tussenvoegsel"
            onChange={(e) => handleInputChange(e, formValues, setFormValues)}
            error={'lastNamePrefix' in formErrors}
            helperText={formErrors.lastNamePrefix}
            margin="dense"
            fullWidth
          />
        </Grid2>

        <Grid2 xs={12} sm={8}>
          <TextField
            value={formValues.lastName}
            name="lastName"
            label="Achternaam"
            onChange={(e) => handleInputChange(e, formValues, setFormValues)}
            error={'lastName' in formErrors}
            helperText={formErrors.lastName}
            margin="dense"
            fullWidth
            required
          />
        </Grid2>

        <Grid2 xs={12}>
          <TextField
            value={formValues.phone}
            name="phone"
            label="Mobiel telefoonnummer"
            onChange={(e) => handleInputChange(e, formValues, setFormValues)}
            error={'phone' in formErrors}
            helperText={formErrors.phone}
            autoComplete="tel"
            margin="dense"
            fullWidth
            required
          />
        </Grid2>

        <Grid2 xs={12}>
          <FormControl
            error={'sex' in formErrors}
            component="fieldset"
            sx={{ m: 3 }}
            variant="standard"
            required
          >
            <FormLabel component="legend">Geslacht</FormLabel>
            <RadioGroup
              value={formValues.sex}
              onChange={(e) => handleRadioChange(e)}
              name='sex'
            >
              <FormControlLabel value="M" control={<Radio />} label="Man" />
              <FormControlLabel value="F" control={<Radio />} label="Vrouw" />
            </RadioGroup>
            <FormHelperText>{formErrors.sex}</FormHelperText>
          </FormControl>
        </Grid2>

        <Grid2 xs={12}>
          <DatePicker
            value={formValues.dateOfBirth}
            name="dateOfBirth"
            label="Geboortedatum"
            onChange={(newValue) => handleDateInputChange(newValue, setFormValues, 'dateOfBirth')}

            autoComplete="bday"
            margin="dense"
            slotProps={{
              textField: {
                fullWidth: true,
                error: 'dateOfBirth' in formErrors,
                helperText: formErrors.dateOfBirth,
                required: true,
              }
            }}
            maxDate={new Date()}
            openTo="year"
          />
        </Grid2>

        <Grid2 xs={12} sm={8}>
        <TextField
          value={formValues.street}
          name="street"
          label="Straat"
          onChange={(e) => handleInputChange(e, formValues, setFormValues)}
          error={'street' in formErrors}
          helperText={formErrors.street}
          autoComplete="street-address"
          margin="dense"
          fullWidth
          required
        />
        </Grid2>

        <Grid2 xs={12} sm={4}>
          <TextField
            value={formValues.houseNumber}
            name="houseNumber"
            label="Huisnummer"
            onChange={(e) => handleInputChange(e, formValues, setFormValues)}
            error={'houseNumber' in formErrors}
            helperText={formErrors.houseNumber}
            margin="dense"
            fullWidth
            required
          />
        </Grid2>

        <Grid2 xs={12} sm={6}>
          <TextField
            value={formValues.postalCode}
            name="postalCode"
            label="Postcode"
            onChange={(e) => handleInputChange(e, formValues, setFormValues)}
            error={'postalCode' in formErrors}
            helperText={formErrors.postalCode}
            autoComplete="postal-code"
            margin="dense"
            fullWidth
            required
          />
        </Grid2>

        <Grid2 xs={12} sm={6}>
          <TextField
            value={formValues.city}
            name="city"
            label="Plaatsnaam"
            onChange={(e) => handleInputChange(e, formValues, setFormValues)}
            error={'city' in formErrors}
            helperText={formErrors.city}
            margin="dense"
            fullWidth
            required
          />
        </Grid2>

        <p>Naar dit adres zullen eventuele producten voor thuisonderzoeken toegestuurd worden.</p>

        {register && (
          <>
            <Grid2 xs={12}>
              <TextField
                value={formValues.iban}
                name="iban"
                label="Bankrekeningnummer (IBAN)"
                onChange={(e) => handleInputChange(e, formValues, setFormValues)}
                error={'iban' in formErrors}
                helperText={formErrors.iban}
                margin="dense"
                fullWidth
                autoComplete="cc-number"
                required
              />
            </Grid2>

            <Grid2 xs={12}>
              <TextField
                value={formValues.ibanInTheNameOf}
                name="ibanInTheNameOf"
                label="Bankrekening (Ten name van)"
                onChange={(e) => handleInputChange(e, formValues, setFormValues)}
                error={'ibanInTheNameOf' in formErrors}
                helperText={formErrors.ibanInTheNameOf}
                margin="dense"
                fullWidth
                required
              />
            </Grid2>
          </>
        )}

        {register && (
          <Grid2 xs={12}>
            <TextField
              value={formValues.referralCode}
              name="referralCode"
              label="Meeneem actie code"
              onChange={(e) => handleInputChange(e, formValues, setFormValues)}
              error={'referralCode' in formErrors}
              helperText={formErrors.referralCode}
              margin="dense"
              fullWidth
              disabled={Boolean(searchParams.get('meeneem-actie-code'))}
            />
            <FormHelperText>Vul hier een code in die u van ons of van een mede deelnemer heeft gehad. Indien u niets heeft ontvangen, laat het veld dan leeg.</FormHelperText>
          </Grid2>
        )}

        <Grid2 xs={12}>
          <FormControl
            error={'mailSubscriptionItems' in formErrors}
            component="fieldset"
            variant="standard"
          >
            <FormLabel component="legend">Op welke locatie wilt u testen?</FormLabel>
            <RadioGroup
              value={formValues.locationMailSubscriptionItem}
              onChange={handleRadioChange}
              name='locationMailSubscriptionItem'
            >
              {mailSubscriptionItems.filter(mailSubscriptionItem => mailSubscriptionItem.type === 'physical_location').map(mailSubscriptionItem => (
                <FormControlLabel
                  key={mailSubscriptionItem.id}
                  value={mailSubscriptionItem['@id']}
                  control={<Radio />}
                  label={mailSubscriptionItem.label}
                />
              ))}
              <FormControlLabel
                key={0}
                value={null}
                control={<Radio />}
                label={'Geen van deze / ik wil alleen thuis testen'}
              />
            </RadioGroup>
            <FormHelperText>{formErrors.mailSubscriptionItems}</FormHelperText>
          </FormControl>

          <FormControl
            error={'mailSubscriptionItems' in formErrors}
            component="fieldset"
            variant="standard"
          >
            <FormLabel component="legend">Wilt u meedoen aan thuis testen?</FormLabel>
            <RadioGroup
              value={formValues.onlineMailSubscriptionItem}
              onChange={handleRadioChange}
              name='onlineMailSubscriptionItem'
            >
              {mailSubscriptionItems.filter(mailSubscriptionItem => mailSubscriptionItem.type === 'online_area').map(mailSubscriptionItem => (
                <FormControlLabel
                  key={mailSubscriptionItem.id}
                  value={mailSubscriptionItem['@id']}
                  control={<Radio />}
                  label={mailSubscriptionItem.label}
                />
              ))}
              <FormControlLabel
                key={0}
                value={null}
                control={<Radio />}
                label={'Geen van deze'}
              />
            </RadioGroup>
            <FormHelperText>{formErrors.mailSubscriptionItems}</FormHelperText>
          </FormControl>
        </Grid2>

        {/*<Grid2 xs={12} mb={2} />*/}

        {register && <Grid2 xs={12}>
          <FormControl
            required
            error={formErrors.acknowledgeRules}
            >
            <FormControlLabel
              control={<Checkbox
                value={formValues.acknowledgeRules}
                name="acknowledgeRules"
                onChange={(e) => handleCheckboxChange(e, formValues, setFormValues)}
                required
              />}
              label="Ik begrijp de Nederlandse taal goed en kan deze spreken, lezen en schrijven. Ik ben me verder ervan bewust dat deelname aan de smaaktesten van Senso altijd geheel vrijwillig en op eigen risico is, zonder verdere verplichtingen."
            />
            { formErrors.acknowledgeRules && <FormHelperText error>{formErrors.acknowledgeRules}</FormHelperText> }
          </FormControl>
        </Grid2>}

        {showSuccessMessage && (
          <Grid2 xs={12} mb={2}>
            <Alert severity="success">
              Je aanpassingen zijn opgeslagen.
            </Alert>
          </Grid2>
        )}

        {showRegistrationSuccessMessage && (
          <Grid2 xs={12} mb={2}>
            <Alert severity="success">
              Bedankt voor je inschrijving. Gebruik je e-mailadres en wachtwoord om in te loggen.
            </Alert>
          </Grid2>
        )}

        {showErrorMessage && (
          <Grid2 xs={12} mb={2}>
            <Alert severity="error">
              Formulier bevat fouten. Pas de getoonde gegevens aan om door te gaan.
            </Alert>
          </Grid2>
        )}

        <Button
          type="button"
          onClick={register ? handleRegisterSubmit : handleEditSubmit}
          variant="contained"
        >
          {register ? 'Verzenden' : 'Opslaan'}
        </Button>
      </Grid2>
    </form>
  );
}

export default UserForm;

UserForm.propTypes = {};

UserForm.defaultProps = {};
